import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Product } from "../../types/Product";
import { formatMoney } from "../../styles/liquidGlass";
import { esSuscripcion } from "@ecommerce/shared-utils";
import { Icon, ImgPh } from "../liquid/LiquidPrimitives";
import QuickAddDialog from "./QuickAddDialog";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildProductWhatsAppUrl } from "../../utils/whatsapp";
import { AppConfigFields } from "../../types/appConfigEntities";
import { resolveVodaProductQuantityContext } from "../../utils/productQuantity";
import { trackMetric } from "@ecommerce/analytics";

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

interface ProductCardProps {
    product: Product;
    variant?: "carousel" | "showcase-small" | "showcase-large" | "carousel-border";
    showDiscount?: boolean;
    discountText?: string;
    onClick?: (productSku: string) => void;
    layout?: "card" | "row";
}

export default function ProductCard({
    product,
    variant,
    showDiscount = false,
    discountText = "Nuevo",
    onClick,
    layout = "card",
}: ProductCardProps) {
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const tiendaInfo = useTiendaInfo();
    const isMobile = useMediaQuery("(max-width:768px)");
    const quantityContext = useMemo(
        () => resolveVodaProductQuantityContext(product, null, minimumPurchase),
        [product],
    );

    const href = `/productos/${product.sku}`;
    const isOutOfStock =
        !quantityContext.isPurchasable ||
        (quantityContext.stock !== null &&
            quantityContext.stock <= (product.stockMinimoPublicacion ?? 0));
    const showPrice = product.mostrarPrecio !== false;
    const isConsultOnly = product.precio === 0;
    const isSuscripcion = esSuscripcion(product.tinCodProductoFormaComercializacion);
    const vchSimboloMoneda = product.sku === "bebedero-botellas-fountric" ? "USD " : "$";
    const vchPrecioFormateado = formatMoney(product.precio).replace("$", vchSimboloMoneda);
    const isLarge = variant === "showcase-large";
    // TODO temporal: sacar cuando "filtro-de-ducha-vitamin-c" tenga cargada la forma de
    // comercializacion correcta (es una venta, no una suscripcion).
    const ocultarSufijoMesTemporal = product.sku === "filtro-de-ducha-vitamin-c";

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        trackMetric("PRODUCT_CLICK", { entityId: product.id.toString() });
        if (!onClick) return;
        const isLeftClick = event.button === 0;
        const isModified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
        if (isLeftClick && !isModified) {
            event.preventDefault();
            onClick(product.sku);
        }
    };

    const handleBagClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(true);
    };

    const handleWhatsAppClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const url = buildProductWhatsAppUrl(product, tiendaInfo?.whatsappNumero);
        trackMetric("PRODUCT_WHATSAPP", { entityId: product.id.toString() });
        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (layout === "row") {
        return (
            <>
                <Link
                    to={href}
                    onClick={handleLinkClick}
                    style={{
                        display: "grid",
                        gridTemplateColumns: "124px 1fr auto",
                        gap: 18,
                        alignItems: "center",
                        padding: 12,
                        borderRadius: 20,
                        textDecoration: "none",
                        color: "inherit",
                        width: "100%",
                        minHeight: 148,
                        background: "rgba(255,255,255,0.80)",
                        border: "1px solid rgba(191,199,210,0.35)",
                        backdropFilter: "blur(16px)",
                        transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,58,92,0.10)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                    }}
                >
                    <div
                        style={{
                            borderRadius: 14,
                            overflow: "hidden",
                            height: 124,
                            background: "#f4f5f7",
                        }}
                    >
                        <ImgPh
                            label={product.nombre}
                            src={product.urlImagenPrincipal}
                            ratio="auto"
                            style={{ height: "100%", borderRadius: 14 }}
                        />
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: isOutOfStock ? "var(--ink-400)" : "#00629c",
                                marginBottom: 6,
                            }}
                        >
                            {isOutOfStock ? "Sin stock" : "Disponible"}
                        </div>
                        <div
                            style={{
                                fontSize: 17,
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                color: "var(--ink-900)",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {product.nombre}
                        </div>
                        <div style={{ marginTop: 6, color: "var(--ink-400)", fontSize: 12 }}>
                            SKU {product.sku}
                        </div>
                        {showPrice && (
                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: isConsultOnly ? 13 : 19,
                                    fontWeight: 700,
                                    color: isConsultOnly ? "var(--ink-500)" : "var(--ink-900)",
                                    fontFamily: isConsultOnly ? undefined : "'SF Mono', monospace",
                                }}
                            >
                                {isConsultOnly ? (
                                    "Consultar precio"
                                ) : (
                                    <>
                                        {vchPrecioFormateado}
                                        {isSuscripcion && (
                                            <>
                                                {" "}
                                                <span
                                                    style={{
                                                        fontWeight: 400,
                                                        fontSize: 13,
                                                        color: "var(--ink-500)",
                                                    }}
                                                >
                                                    / mes
                                                </span>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                        aria-label={
                            isConsultOnly ? "Consultar por WhatsApp" : "Elegir variante y cantidad"
                        }
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: isConsultOnly ? "#25D366" : "#00629c",
                            color: "#ffffff",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            boxShadow: "0 4px 14px rgba(0,58,92,0.25)",
                            flexShrink: 0,
                        }}
                    >
                        {isConsultOnly ? (
                            <Icon.whatsapp color="#ffffff" />
                        ) : (
                            <Icon.bag color="#ffffff" />
                        )}
                    </button>
                </Link>

                {!isConsultOnly && (
                    <QuickAddDialog
                        open={quickAddOpen}
                        product={product}
                        onClose={() => setQuickAddOpen(false)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <Link
                to={href}
                onClick={handleLinkClick}
                style={{
                    position: "relative",
                    borderRadius: 20,
                    overflow: "hidden",
                    cursor: "pointer",
                    background: "rgba(255,255,255,0.80)",
                    border: "1px solid rgba(255,255,255,0.60)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 4px 20px rgba(0,58,92,0.06)",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    display: "block",
                    width: "100%",
                }}
                onMouseEnter={(event) => {
                    event.currentTarget.style.transform = "translateY(-6px)";
                    event.currentTarget.style.boxShadow = "0 16px 40px rgba(0,58,92,0.12)";
                }}
                onMouseLeave={(event) => {
                    event.currentTarget.style.transform = "translateY(0)";
                    event.currentTarget.style.boxShadow = "0 4px 20px rgba(0,58,92,0.06)";
                }}
            >
                <div
                    style={{
                        position: "relative",
                        aspectRatio: isLarge ? "1/1.1" : "1/1.05",
                        background: "#f4f5f7",
                        borderRadius: 20,
                        overflow: "hidden",
                    }}
                >
                    <ImgPh
                        label={product.nombre}
                        ratio="auto"
                        src={product.urlImagenPrincipal}
                        style={{
                            height: "100%",
                            borderRadius: 0,
                            background: "#f4f5f7",
                        }}
                    />

                    {(showDiscount || isOutOfStock) && (
                        <div
                            style={{
                                position: "absolute",
                                top: 12,
                                left: 12,
                                padding: "5px 12px",
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.10em",
                                textTransform: "uppercase",
                                background: isOutOfStock ? "rgba(26,28,30,0.75)" : "#00629c",
                                color: "#ffffff",
                                backdropFilter: "blur(8px)",
                            }}
                        >
                            {isOutOfStock ? "Sin stock" : discountText}
                        </div>
                    )}

                    {!isMobile && (
                        <div
                            style={{
                                position: "absolute",
                                left: 10,
                                right: 10,
                                bottom: 10,
                                padding: "11px 14px",
                                borderRadius: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                background: "rgba(255,255,255,0.88)",
                                backdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.60)",
                                boxShadow: "0 4px 16px rgba(0,58,92,0.08)",
                            }}
                        >
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: "var(--ink-700)",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    {product.nombre}
                                </div>
                                {showPrice && (
                                    <div
                                        style={{
                                            fontSize: isConsultOnly ? 11 : 14,
                                            fontWeight: 700,
                                            marginTop: 2,
                                            color: isConsultOnly
                                                ? "var(--ink-500)"
                                                : "var(--ink-900)",
                                            fontFamily: isConsultOnly
                                                ? undefined
                                                : "'SF Mono', monospace",
                                        }}
                                    >
                                        {isConsultOnly ? (
                                            "Consultar precio"
                                        ) : (
                                            <>
                                                {vchPrecioFormateado}
                                                {isSuscripcion && (
                                                    <>
                                                        {" "}
                                                        <span
                                                            style={{
                                                                fontWeight: 400,
                                                                fontSize: 11,
                                                                color: "var(--ink-500)",
                                                            }}
                                                        >
                                                            / mes
                                                        </span>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                                aria-label={
                                    isConsultOnly
                                        ? "Consultar por WhatsApp"
                                        : "Elegir variante y cantidad"
                                }
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 10,
                                    background: isConsultOnly ? "#25D366" : "#00629c",
                                    color: "#ffffff",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 4px 12px rgba(0,58,92,0.25)",
                                    flexShrink: 0,
                                }}
                            >
                                {isConsultOnly ? (
                                    <Icon.whatsapp color="#ffffff" />
                                ) : (
                                    <Icon.bag color="#ffffff" />
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {isMobile && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            padding: "10px 12px 12px",
                        }}
                    >
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    fontSize: 12.5,
                                    fontWeight: 500,
                                    color: "var(--ink-700)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                {product.nombre}
                            </div>
                            {showPrice && (
                                <div
                                    style={{
                                        fontSize: isConsultOnly ? 12 : 15,
                                        fontWeight: 700,
                                        marginTop: 3,
                                        color: isConsultOnly ? "var(--ink-500)" : "var(--ink-900)",
                                        fontFamily: isConsultOnly
                                            ? undefined
                                            : "'SF Mono', monospace",
                                    }}
                                >
                                    {isConsultOnly ? (
                                        "Consultar precio"
                                    ) : (
                                        <>
                                            {vchPrecioFormateado}
                                            {isSuscripcion && (
                                                <>
                                                    {" "}
                                                    <span
                                                        style={{
                                                            fontWeight: 400,
                                                            fontSize: 12,
                                                            color: "var(--ink-500)",
                                                        }}
                                                    >
                                                        / mes
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                            aria-label={
                                isConsultOnly
                                    ? "Consultar por WhatsApp"
                                    : "Elegir variante y cantidad"
                            }
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 12,
                                background: isConsultOnly ? "#25D366" : "#00629c",
                                color: "#ffffff",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 4px 12px rgba(0,58,92,0.22)",
                                flexShrink: 0,
                            }}
                        >
                            {isConsultOnly ? (
                                <Icon.whatsapp size={18} color="#ffffff" />
                            ) : (
                                <Icon.bag size={18} color="#ffffff" />
                            )}
                        </button>
                    </div>
                )}
            </Link>

            {!isConsultOnly && (
                <QuickAddDialog
                    open={quickAddOpen}
                    product={product}
                    onClose={() => setQuickAddOpen(false)}
                />
            )}
        </>
    );
}
